import {useContext, useEffect, useRef, useState} from 'react';
import mapboxgl, {Map, MapMouseEvent, Popup} from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { config } from '../config';


import {
    getVenueLabelsHighlightedLayer,
    getVenuePinsHighlightedLayer
} from '../maputils';
import {MapViewContext} from "../contexts/MapViewContext";
import {bboxToTileList} from "../utils/bboxToTileList";
import {useLocation, useNavigate} from "react-router-dom";

mapboxgl.accessToken = 'pk.eyJ1IjoiY2FsZW5kcmUiLCJhIjoiY2lwMDk4NThhMDJpOHVmbTR6dWp3ZXIzbCJ9.QoQKozHFNWDVAiQy2c_AnQ';

// Meta info response shape
interface MapMeta {
    mapCentroid: [number, number];
    mapBbox?: [number, number, number, number];
    zoom?: number;
    name?: string;
}

interface MapboxMapComponentProps {
    mapId: string;
}

const MapboxMapComponent: React.FC<MapboxMapComponentProps> = ({mapId}) => {
    const mapboxUser = 'calendre';
    const theme = 'cmavgem7p00c901sc1l80fwj9';

    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<Map | null>(null);
    const [mapMeta, setMapMeta] = useState<MapMeta | null>(null);

    const navigate = useNavigate();
    const location = useLocation();

    const {
        setMapTileCoords,
        setViewportBounds,
        hoveredStateId,
        setMapName,
        selectedTagIds,
        selectedCategoryIds,
        sidebarCollapsed,
        searchQuery
    } = useContext(MapViewContext);

    useEffect(() => {
        const fetchMapMeta = async () => {
            try {
                console.log("config",config);
                console.log("directoryTilesUrl",config.directoryTilesUrl);
                const res = await fetch(`${config.mapsManagerApiUrl}/maps/${mapId}/metaInfo`);
                const data: MapMeta = await res.json();

                const params = new URLSearchParams(window.location.search);
                const zoomParam = params.get('zoom');
                const latParam = params.get('lat');
                const lngParam = params.get('lng');

                const zoom = parseFloat(zoomParam ?? '');
                const lat = parseFloat(latParam ?? '');
                const lng = parseFloat(lngParam ?? '');

                const isValidZoom = !isNaN(zoom) && zoom > 0 && zoom <= 22;
                const isValidLat = !isNaN(lat) && lat >= -90 && lat <= 90;
                const isValidLng = !isNaN(lng) && lng >= -180 && lng <= 180;

                if (isValidZoom && isValidLat && isValidLng) {
                    data.mapCentroid = [lng, lat];
                    data.zoom = zoom;
                    data.mapBbox = undefined;
                }

                setMapMeta(data);
                setMapName(data.name);
            } catch (err) {
                console.error('Failed to fetch map metaInfo:', err);
            }
        };

        fetchMapMeta();
    }, [mapId]);

    useEffect(() => {
        if (mapMeta?.name) {
            document.title = `Live XYZ map: ${mapMeta.name}`;
        }
    }, [mapMeta?.name]);

    useEffect(() => {
        if (!mapMeta || !mapContainerRef.current) return;

        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: `mapbox://styles/${mapboxUser}/${theme}`,
            center: mapMeta.mapCentroid,
            zoom: mapMeta?.zoom || 13,
        });

        const map = mapRef.current;

        map.on('load', () => {
            const params = new URLSearchParams({map_id: mapId});
            if (selectedTagIds.length > 0) {
                params.set('tag_ids', selectedTagIds.join(','));
            }
            if (selectedCategoryIds.length > 0) {
                params.set('categories_ids', selectedCategoryIds.join(','));
            }

            if (searchQuery !== null && searchQuery.length > 3) {
                params.set('search', searchQuery);
            }

            const newUrl = `${config.directoryTilesUrl}/get_live_tiles?${params.toString()}`;

            map.addSource('live_tiles', {
                type: 'vector',
                attribution: 'properties',
                url: newUrl
            });

            // @ts-ignore
            map.addLayer(getVenuePinsHighlightedLayer());
            // @ts-ignore
            map.addLayer(getVenueLabelsHighlightedLayer(theme));

            if (mapMeta.mapBbox) {
                map.fitBounds(
                    [
                        [mapMeta.mapBbox[0], mapMeta.mapBbox[1]],
                        [mapMeta.mapBbox[2], mapMeta.mapBbox[3]]
                    ],
                    {padding: 40, duration: 0}
                );
            }

            // Update positioning
            const zoom = Math.floor(map.getZoom());
            const bounds = map.getBounds();
            const tiles = bboxToTileList(bounds, zoom);
            setMapTileCoords(tiles);
            setViewportBounds(bounds);

            // Hover popup
            let hoverPopup: Popup | null = null;
            map.on('mouseenter', 'live_tiles', (e: MapMouseEvent) => {
                if (!e.features || !e.features.length) return;

                map.getCanvas().style.cursor = 'pointer';
                const feature = e.features[0];
                const p = feature.properties as any;
                const coordinates = [...(feature.geometry as any).coordinates];
                const name = p.resolved_name || 'Unnamed';

                // Fix wrap
                while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                    coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                }

                hoverPopup = new mapboxgl.Popup({
                    closeButton: false,
                    closeOnClick: false
                })
                    // @ts-ignore
                    .setLngLat(coordinates)
                    .setHTML(`<strong>${name}</strong>`)
                    .addTo(map);
            });

            map.on('mouseleave', 'live_tiles', () => {
                map.getCanvas().style.cursor = '';
                if (hoverPopup) {
                    hoverPopup.remove();
                    hoverPopup = null;
                }
            });

            map.on('moveend', () => {
                const zoom = Math.floor(map.getZoom());
                const bounds = map.getBounds();
                const tiles = bboxToTileList(bounds, zoom);
                setMapTileCoords(tiles);
                setViewportBounds(bounds);

                const center = map.getCenter();
                const lat = center.lat.toFixed(6);
                const lng = center.lng.toFixed(6);

                const params = new URLSearchParams(location.search);
                params.set('zoom', map.getZoom().toFixed(2));
                params.set('lat', lat);
                params.set('lng', lng);

                navigate(`${location.pathname}?${params.toString()}`, {replace: true});
            });
        });

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
            }
        };
    }, [mapId, mapMeta, theme]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !map.isStyleLoaded()) return;

        const source = map.getSource('live_tiles') as mapboxgl.VectorTileSource;
        if (source) {
            const params = new URLSearchParams({map_id: mapId});
            if (selectedTagIds.length > 0) {
                params.set('tag_ids', selectedTagIds.join(','));
            }

            if (selectedCategoryIds.length > 0) {
                params.set('categories_ids', selectedCategoryIds.join(','));
            }

            if (searchQuery !== null && searchQuery.length > 3) {
                params.set('search', searchQuery);
            }

            const newUrl = `${config.directoryTilesUrl}/get_live_tiles?${params.toString()}`;

            map.removeLayer('live_tiles');
            map.removeLayer('live_tiles_labels');
            map.removeSource('live_tiles');

            map.addSource('live_tiles', {
                type: 'vector',
                attribution: 'properties',
                url: newUrl
            });

            // @ts-ignore
            map.addLayer(getVenuePinsHighlightedLayer());
            // @ts-ignore
            map.addLayer(getVenueLabelsHighlightedLayer(theme));
        }
    }, [searchQuery, selectedTagIds, selectedCategoryIds, mapId]);

    useEffect(() => {
        const map = mapRef.current;

        if (!map || !map.isStyleLoaded()) return;

        let currentFeatureId: string | number | undefined;
        let feedHoverPopup: mapboxgl.Popup | null = null;

        if (hoveredStateId) {
            const features = map.querySourceFeatures('live_tiles', {
                sourceLayer: 'live_tiles',
                filter: ['==', 'state_id', hoveredStateId],
            });

            const feature = features[0];
            if (feature && feature.properties) {
                currentFeatureId = feature.id;

                // Optional: highlight feature
                map.setFeatureState(
                    {
                        source: 'live_tiles',
                        sourceLayer: 'live_tiles',
                        id: currentFeatureId,
                    },
                    {hover: true}
                );

                // Show popup
                const coordinates = [...(feature.geometry as any).coordinates];
                const name = feature.properties.resolved_name || 'Unnamed';

                feedHoverPopup = new mapboxgl.Popup({
                    closeButton: false,
                    closeOnClick: false,
                })
                    // @ts-ignore
                    .setLngLat(coordinates)
                    .setHTML(`<strong>${name}</strong>`)
                    .addTo(map);
            }
        }

        return () => {
            if (map && currentFeatureId != null) {
                map.setFeatureState(
                    {
                        source: 'live_tiles',
                        sourceLayer: 'live_tiles',
                        id: currentFeatureId,
                    },
                    {hover: false}
                );
            }

            if (feedHoverPopup) {
                feedHoverPopup.remove();
                feedHoverPopup = null;
            }
        };
    }, [hoveredStateId]);

    useEffect(() => {
        const map = mapRef.current;
        const timeout = setTimeout(() => {
            map?.resize();
        }, 10);

        return () => clearTimeout(timeout);
    }, [sidebarCollapsed]);


    return <div ref={mapContainerRef} style={{height: '100%'}}/>;
};


export default MapboxMapComponent;
